/* eslint-disable indent */
import React, { Fragment, useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../../overmind';
import { Anchor, CheckboxCard, Chip, ChipWrapper, ListItemLayout, ListLayout, search, SearchBar } from '@keplerco/core';
import classNames from 'classnames';
import { SkeletonLoader } from '../../../components/general/loading-state/loaders/skeleton-loader/skeleton-loader';
import { CareerPathSkillSet } from '../../../models';
import { AssessmentRequest } from '../../../overmind/actions';

interface AssessmentsWizardFieldOfPractice {
  careerPathSlug: string;
  careerPathName: string;
  checked: boolean;

  jobsIds: number[] | undefined;
  skillSets: CareerPathSkillSet[];
}

export interface AssessmentsWizardFieldsOfPractice {
  assessmentSlug: string;
  careerPathSlugs: string[];
}
enum ErrorMessage {
  NoFieldsOfPractice = 'At least one field of practice must be selected',
}

const CUSTOM_THEME = { backgroundColor: 'background', borderColor: 'baby-blue', color: 'text' };

export function AssessmentFOPWidget({ details, onSaveAndContinue }: { details: AssessmentRequest | undefined; onSaveAndContinue: () => void }): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [loading, setLoading] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const [fieldsOfPractice, setFieldsOfPractice] = useState<AssessmentsWizardFieldOfPractice[]>([]);
  const [searchedFieldsOfPractice, setSearchedFieldsOfPractice] = useState<AssessmentsWizardFieldOfPractice[]>([]);
  const [dirty, setDirty] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>();

  useEffect(() => {
    async function getData() {
      if (!details?.slug) return;

      setLoading(true);

      const careerPathSlugsResponse = await actions.getAssessmentCareerPathSlugs(details.slug);
      const careerPaths = (await actions.getCompanyCareerPaths(companyVariables.slug!)) ?? [];

      const nextFieldsOfPractice: AssessmentsWizardFieldOfPractice[] = careerPaths.map(careerPath => ({
        ...careerPath,
        checked: careerPathSlugsResponse?.careerPathSlugs.includes(careerPath.careerPathSlug) ?? false,
      }));

      setFieldsOfPractice(nextFieldsOfPractice);

      setLoading(false);
    }

    getData();
  }, [companyVariables.slug]);

  useEffect(() => {
    setSearchedFieldsOfPractice(search(fieldsOfPractice, query, 'careerPathName'));
  }, [query, fieldsOfPractice]);

  useEffect(() => {
    setErrorMessage(!fieldsOfPractice.filter(item => item.checked).length ? ErrorMessage.NoFieldsOfPractice : undefined);
  }, [fieldsOfPractice]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        <SkeletonLoader height="550px" />

        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
          <SkeletonLoader width="150px" height="25px" />
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="card">
        <SearchBar label="Search" onInput={event => setQuery((event.target as HTMLInputElement).value)} responsive />

        <div style={{ marginTop: 15, marginBottom: 15, maxHeight: 400, overflowY: 'auto' }}>
          {/* TODO: EntitySelectionList */}
          <ListLayout>
            {searchedFieldsOfPractice.map(fieldOfPractice => (
              <ListItemLayout key={fieldOfPractice.careerPathSlug}>
                <CheckboxCard
                  id={fieldOfPractice.careerPathSlug}
                  checked={fieldOfPractice.checked}
                  onChange={() => {
                    setFieldsOfPractice(currentState => {
                      const nextState: AssessmentsWizardFieldOfPractice[] = structuredClone(currentState);
                      const nextItem = nextState.find(item => item.careerPathSlug === fieldOfPractice.careerPathSlug);
                      if (!!nextItem) nextItem.checked = !nextItem.checked;
                      return nextState;
                    });
                  }}
                >
                  <div className="card" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 5 }}>
                    <label htmlFor={fieldOfPractice.careerPathSlug}>{fieldOfPractice.careerPathName}</label>

                    <ChipWrapper className="ChipWrapper" style={{ justifyContent: 'flex-end' }}>
                      {!!fieldOfPractice.skillSets.length && <Chip customTheme={CUSTOM_THEME}>{fieldOfPractice.skillSets.length} skills</Chip>}

                      {!!fieldOfPractice.jobsIds?.length && <Chip customTheme={CUSTOM_THEME}>{fieldOfPractice.jobsIds?.length} jobs</Chip>}

                      {!fieldOfPractice.jobsIds?.length && !fieldOfPractice.skillSets.length && <Chip customTheme={{ ...CUSTOM_THEME, borderColor: 'borders' }}>Empty</Chip>}
                    </ChipWrapper>
                  </div>
                </CheckboxCard>
              </ListItemLayout>
            ))}
          </ListLayout>
        </div>

        <div className={classNames('formErrorMessage', { invisible: !dirty || !errorMessage })}>{errorMessage}&nbsp;</div>
      </div>

      <div style={{ marginTop: 15, display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
        <Anchor
          arrow
          onClick={async () => {
            setDirty(true);

            if (!details?.slug) return;
            if (!!errorMessage) return;

            setLoading(true);

            await actions.postAssessmentCareerPathSlugs({
              assessmentSlug: details.slug,
              careerPathSlugs: fieldsOfPractice.filter(fieldOfPractice => fieldOfPractice.checked).map(fieldOfPractice => fieldOfPractice.careerPathSlug),
            });

            setLoading(false);
            onSaveAndContinue();
          }}
        >
          Save & Continue
        </Anchor>
      </div>
    </Fragment>
  );
}
